import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';

const EventDetail = ({ eventId: propEventId }) => {
  const [event, setEvent] = useState(null);
  const [userRegistration, setUserRegistration] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [unregistering, setUnregistering] = useState(false);
  const [submittingQuestionnaire, setSubmittingQuestionnaire] = useState(false);
  const [questionnaireData, setQuestionnaireData] = useState({});
  const [toast, setToast] = useState(null);
  const [confirmUnregister, setConfirmUnregister] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [plusOneForm, setPlusOneForm] = useState({ first_name: '', last_name: '', email: '', image_rights_accepted: false, rules_accepted: false });
  const [submittingPlusOne, setSubmittingPlusOne] = useState(false);
  const [showPlusOneForm, setShowPlusOneForm] = useState(false);
  // Questionnaires des +1 : { [registrationId]: { fieldName: value } }
  const [plusOneQuestionnaires, setPlusOneQuestionnaires] = useState({});
  // Etat de soumission par +1 : { [registrationId]: boolean }
  const [submittingPlusOneQuestionnaire, setSubmittingPlusOneQuestionnaire] = useState({});
  const [cancellingPlusOne, setCancellingPlusOne] = useState({});

  const getEventId = () => {
    if (propEventId) return propEventId;
    const match = window.location.pathname.match(/\/events\/(\d+)/);
    return match ? match[1] : null;
  };
  const eventId = getEventId();

  const csrf = () => document.querySelector('[name="csrf-token"]').content;

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  useEffect(() => {
    if (eventId) loadEvent();
    else setLoading(false);
  }, [eventId]);

  const loadEvent = async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/v1/events/${eventId}`, {
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() }
      });
      const data = await res.json();
      if (data.success) {
        setEvent(data.event);
        setUserRegistration(data.user_registration);
        setPermissions(data.permissions);
        // Pré-remplir le questionnaire avec les réponses existantes
        if (data.user_registration?.custom_data) {
          setQuestionnaireData(data.user_registration.custom_data);
        }
        // Pré-remplir les questionnaires des +1
        const plusOnes = data.user_registration?.plus_ones || [];
        if (plusOnes.length > 0) {
          const map = {};
          plusOnes.forEach(po => {
            if (po.custom_data) map[po.registration_id] = po.custom_data;
          });
          setPlusOneQuestionnaires(prev => ({ ...prev, ...map }));
        }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Étape 3 : soumettre le questionnaire
  const handleSubmitQuestionnaire = async (e) => {
    e.preventDefault();
    setSubmittingQuestionnaire(true);
    try {
      const res = await fetch(`/api/v1/events/${eventId}/registration`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() },
        body: JSON.stringify({ custom_fields: questionnaireData })
      });
      const data = await res.json();
      if (data.success) {
        setUserRegistration(data.registration);
        showToast(data.message);
      } else {
        showToast(data.message, 'error');
      }
    } catch { showToast('Erreur lors de la soumission', 'error'); }
    finally { setSubmittingQuestionnaire(false); }
  };

  const handleUnregister = async () => {
    setUnregistering(true);
    setConfirmUnregister(false);
    try {
      const res = await fetch(`/api/v1/events/${eventId}/register`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() }
      });
      const data = await res.json();
      if (data.success) {
        setUserRegistration(null);
        setQuestionnaireData({});
        loadEvent();
        showToast(data.message);
      } else {
        showToast(data.message, 'error');
      }
    } catch { showToast('Erreur lors de la désinscription', 'error'); }
    finally { setUnregistering(false); }
  };

  const handleAddPlusOne = async (e) => {
    e.preventDefault();
    if (!plusOneForm.image_rights_accepted || !plusOneForm.rules_accepted) {
      showToast('Vous devez accepter le droit à l\'image et le règlement intérieur pour votre invité(e)', 'error');
      return;
    }
    setSubmittingPlusOne(true);
    try {
      const res = await fetch(`/api/v1/events/${eventId}/add_plus_one`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() },
        body: JSON.stringify(plusOneForm)
      });
      const data = await res.json();
      if (data.success) {
        showToast(data.message);
        // Réinitialiser le formulaire pour pouvoir en ajouter un autre
        setPlusOneForm({ first_name: '', last_name: '', email: '', image_rights_accepted: false, rules_accepted: false });
        setShowPlusOneForm(false);
        loadEvent();
      } else {
        showToast(data.message, 'error');
      }
    } catch { showToast('Erreur réseau, veuillez réessayer', 'error'); }
    finally { setSubmittingPlusOne(false); }
  };

  const handleCancelPlusOne = async (registrationId) => {
    if (!registrationId) return;
    if (!window.confirm('Annuler l\'inscription de cet invité ?')) return;
    setCancellingPlusOne(p => ({ ...p, [registrationId]: true }));
    try {
      const res = await fetch(`/api/v1/events/${eventId}/cancel_plus_one`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() },
        body: JSON.stringify({ registration_id: registrationId })
      });
      const data = await res.json();
      if (data.success) {
        showToast(data.message);
        loadEvent();
      } else {
        showToast(data.message, 'error');
      }
    } catch { showToast('Erreur réseau, veuillez réessayer', 'error'); }
    finally { setCancellingPlusOne(p => ({ ...p, [registrationId]: false })); }
  };

  const handleSubmitPlusOneQuestionnaire = async (e, plusOneReg) => {
    e.preventDefault();
    if (!plusOneReg) return;
    const regId = plusOneReg.registration_id;
    setSubmittingPlusOneQuestionnaire(p => ({ ...p, [regId]: true }));
    try {
      const res = await fetch(`/api/v1/events/${eventId}/registration`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() },
        body: JSON.stringify({ registration_id: regId, custom_fields: plusOneQuestionnaires[regId] || {} })
      });
      const data = await res.json();
      if (data.success) {
        showToast('Questionnaire de l\'invité(e) enregistré ✅');
        loadEvent();
      } else {
        showToast(data.message, 'error');
      }
    } catch { showToast('Erreur lors de la soumission', 'error'); }
    finally { setSubmittingPlusOneQuestionnaire(p => ({ ...p, [regId]: false })); }
  };

  const formatDateShort = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const formatTime = (d) => d ? new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';

  const statusConfig = {
    registered: { icon: '✅', label: 'Inscrit(e)', color: '#15803d', bg: '#f0fdf4', border: '#86efac' },
    waitlist:   { icon: '⏳', label: "Liste d'attente", color: '#b45309', bg: '#fffbeb', border: '#fcd34d' },
    cancelled:  { icon: '✖️', label: 'Annulé', color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' },
    attended:   { icon: '🎉', label: 'Présent(e)', color: '#1d4ed8', bg: '#eff6ff', border: '#93c5fd' },
  };

  const eventStatusConfig = {
    draft:              { label: 'Brouillon', color: '#6b7280', bg: '#f3f4f6' },
    pending_validation: { label: 'En validation', color: '#b45309', bg: '#fffbeb' },
    published:          { label: 'Publié', color: '#15803d', bg: '#f0fdf4' },
    completed:          { label: 'Terminé', color: '#1d4ed8', bg: '#eff6ff' },
    cancelled:          { label: 'Annulé', color: '#dc2626', bg: '#fef2f2' },
  };

  const inputS = {
    width: '100%', boxSizing: 'border-box',
    background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.20)',
    borderRadius: '10px', padding: '11px 14px',
    color: '#2c0e16', fontSize: '14px', outline: 'none',
  };

  // ── Composant champ de questionnaire ──────────────────────────────────────
  const QuestionnaireField = ({ field, val: externalVal, onChange: externalOnChange }) => {
    const val = externalVal !== undefined ? externalVal : (questionnaireData[field.name] || '');
    const set = externalOnChange || ((v) => setQuestionnaireData(p => ({ ...p, [field.name]: v })));

    if (field.type === 'select') {
      const options = (field.options || [])
        .map(o => typeof o === 'string' ? { value: o, label: o } : o)
        .filter(o => !o.full || o.value === val);
      return (
        <select style={inputS} value={val} onChange={e => set(e.target.value)} required={field.required}>
          <option value="">Choisir…</option>
          {options.map((o, j) => {
            const remaining = o.max_responses
              ? o.value === val && o.full
                ? ` (option conservée, complet)`
                : o.remaining_responses != null
                  ? ` (${o.remaining_responses} place${o.remaining_responses > 1 ? 's' : ''} restante${o.remaining_responses > 1 ? 's' : ''})`
                  : ` (${o.max_responses} max)`
              : '';
            return <option key={j} value={o.value}>{o.label}{remaining}</option>;
          })}
        </select>
      );
    }
    if (field.type === 'textarea') {
      return <textarea style={{ ...inputS, minHeight: 80, resize: 'vertical' }} value={val} onChange={e => set(e.target.value)} required={field.required} placeholder={field.placeholder} />;
    }
    if (field.type === 'checkbox') {
      if (field.options && field.options.length > 0) {
        const options = (field.options || []).map(o => typeof o === 'string' ? { value: o, label: o } : o);
        const currentValues = Array.isArray(val) ? val : (val ? [val] : []);
        const knownOptions = options.map(o => o.value || o.label);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {options.map((o, j) => {
              const optVal = o.value || o.label;
              const isChecked = currentValues.includes(optVal);
              return (
                <label key={j} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <input type="checkbox" checked={isChecked} onChange={e => {
                    const newValues = e.target.checked ? [...currentValues, optVal] : currentValues.filter(v => v !== optVal);
                    set(newValues);
                  }} style={{ width: 18, height: 18, accentColor: '#7c2837' }} />
                  <span style={{ color: '#4a2030', fontSize: '0.9rem' }}>{o.label}</span>
                </label>
              );
            })}
            {field.allow_other && (() => {
              const otherValue = currentValues.find(v => !knownOptions.includes(v));
              const otherChecked = otherValue !== undefined;
              return (
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                    <input type="checkbox" checked={otherChecked} onChange={e => {
                      if (e.target.checked) { set([...currentValues, '']); }
                      else { set(currentValues.filter(v => knownOptions.includes(v))); }
                    }} style={{ width: 18, height: 18, accentColor: '#7c2837' }} />
                    <span style={{ color: '#4a2030', fontSize: '0.9rem' }}>Autre</span>
                  </label>
                  {otherChecked && (
                    <input type="text" style={{ ...inputS, marginLeft: 28, marginTop: 4, maxWidth: 280 }} placeholder="Précisez..." value={otherValue || ''} onChange={e => {
                      const newValues = currentValues.filter(v => knownOptions.includes(v));
                      newValues.push(e.target.value);
                      set(newValues);
                    }} />
                  )}
                </div>
              );
            })()}
          </div>
        );
      }
      return (
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input type="checkbox" checked={val === true || val === 'true'} onChange={e => set(e.target.checked)} style={{ width: 18, height: 18, accentColor: '#7c2837' }} />
          <span style={{ color: '#4a2030', fontSize: '0.9rem' }}>{field.placeholder || 'Oui'}</span>
        </label>
      );
    }
    return <input type="text" style={inputS} value={val} onChange={e => set(e.target.value)} required={field.required} placeholder={field.placeholder} />;
  };

  // ── Étapes d'un inscrit ────────────────────────────────────────────────────
  const getSteps = (reg, evt) => {
    if (!reg) return [];
    const steps = [
      { key: 'register', icon: '✅', label: "Inscription", done: true }
    ];
    if (reg.payment_amount > 0) {
      steps.push({ key: 'payment', icon: reg.paid ? '✅' : '💳', label: 'Paiement', done: reg.paid });
    }
    if (reg.questionnaire_required) {
      steps.push({ key: 'questionnaire', icon: reg.questionnaire_completed ? '✅' : '📋', label: 'Questionnaire', done: reg.questionnaire_completed });
    }
    return steps;
  };

  if (loading) return (
    <div style={{ background: '#f7eef2', minHeight: '100vh', paddingTop: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 48, height: 48, border: '4px solid rgba(124,40,55,0.2)', borderTop: '4px solid #7c2837', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (!event) return (
    <div style={{ background: '#f7eef2', minHeight: '100vh', paddingTop: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 24 }}>
      <div style={{ fontSize: '4rem' }}>🔍</div>
      <h2 style={{ color: '#2c0e16', fontWeight: 800, margin: 0 }}>Événement introuvable</h2>
      <a href="/events" style={{ background: 'linear-gradient(135deg,#7c2837,#a8384e)', color: '#fff', padding: '12px 28px', borderRadius: '10px', textDecoration: 'none', fontWeight: 700 }}>← Retour aux événements</a>
    </div>
  );

  const reg = userRegistration;
  const regStatus = reg ? (statusConfig[reg.status] || statusConfig.registered) : null;
  const evStatus = eventStatusConfig[event.status] || eventStatusConfig.draft;
  const fillPct = event.capacity > 0 ? Math.min(100, Math.round(event.registered_count / event.capacity * 100)) : 0;
  const steps = getSteps(reg, event);
  const customFields = event.custom_fields || [];

  return (
    <div className="event-mobile-shell event-detail-page" style={{ background: '#f7eef2', minHeight: '100vh', paddingTop: '80px', paddingBottom: '80px', fontFamily: 'inherit' }}>

      {/* Toast */}
      {toast && (
        <div className="event-detail-toast" style={{ position: 'fixed', top: 24, right: 24, zIndex: 9999, background: toast.type === 'error' ? '#7c1c2a' : '#1a3a2a', border: `1px solid ${toast.type === 'error' ? '#c0392b' : '#27ae60'}`, color: '#fff', padding: '14px 24px', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', fontWeight: 600, fontSize: '0.95rem', maxWidth: 380 }}>
          {toast.type === 'error' ? '❌' : '✅'} {toast.message}
        </div>
      )}

      {/* Modal confirmation désinscription */}
      {confirmUnregister && (
        <div className="event-detail-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(44,14,22,0.5)', zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div className="event-detail-modal-card" style={{ background: '#fff', borderRadius: 20, padding: 32, maxWidth: 420, width: '100%', boxShadow: '0 24px 64px rgba(44,14,22,0.25)' }}>
            <h3 style={{ color: '#2c0e16', margin: '0 0 12px', fontWeight: 800, fontSize: '1.2rem' }}>Annuler mon inscription</h3>
            <p style={{ color: '#7a4050', margin: '0 0 24px', fontSize: '0.95rem' }}>Êtes-vous sûr(e) de vouloir annuler votre inscription à <strong>{event.title}</strong> ?</p>
            <div className="event-modal-actions" style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmUnregister(false)} style={{ background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.2)', color: '#2c0e16', padding: '10px 22px', borderRadius: 10, cursor: 'pointer', fontWeight: 600 }}>Non</button>
              <button onClick={handleUnregister} disabled={unregistering} style={{ background: '#dc2626', border: 'none', color: '#fff', padding: '10px 22px', borderRadius: 10, cursor: 'pointer', fontWeight: 700 }}>
                {unregistering ? 'En cours…' : 'Oui, annuler'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="event-mobile-container event-detail-container" style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px' }}>

        {/* Retour */}
        <a href="/events" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#7a4050', textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem', marginBottom: 28, padding: '8px 16px', background: '#fff', borderRadius: 10, border: '1px solid rgba(124,40,55,0.15)' }}>
          ← Tous les événements
        </a>

        {/* ── HERO ─────────────────────────────────────────────────────── */}
        <div className="event-detail-hero" style={{ background: 'linear-gradient(135deg,#7c2837 0%,#943143 50%,#a8384e 100%)', borderRadius: 24, padding: '40px 44px', marginBottom: 24, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -80, right: -80, width: 320, height: 320, background: 'rgba(255,255,255,0.07)', borderRadius: '50%', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -60, left: -60, width: 240, height: 240, background: 'rgba(255,255,255,0.05)', borderRadius: '50%', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div className="event-detail-hero-badges" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
              <span style={{ background: 'rgba(255,255,255,0.18)', color: '#fff', padding: '5px 14px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 700 }}>{evStatus.label}</span>
              {event.club && <span style={{ background: 'rgba(255,255,255,0.12)', color: '#fff', padding: '5px 14px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 600 }}>🏛️ {event.club.name}</span>}
              {reg && <span style={{ background: regStatus.bg, color: regStatus.color, border: `1px solid ${regStatus.border}`, padding: '5px 14px', borderRadius: 20, fontSize: '0.82rem', fontWeight: 700 }}>{regStatus.icon} {regStatus.label}{reg.waitlist_position ? ` · #${reg.waitlist_position}` : ''}</span>}
            </div>
            <h1 style={{ color: '#fff', fontSize: 'clamp(1.7rem,4vw,2.6rem)', fontWeight: 800, margin: '0 0 10px', lineHeight: 1.2 }}>{event.title}</h1>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.95rem', margin: '0 0 24px' }}>Organisé par <strong style={{ color: '#fff' }}>{event.organizer?.name}</strong></p>
            <div className="event-detail-hero-meta" style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {[
                { icon: '📅', text: `${formatDateShort(event.start_date)} · ${formatTime(event.start_date)}` },
                event.end_date && { icon: '🏁', text: formatDateShort(event.end_date) },
                { icon: '📍', text: event.location },
                { icon: '👥', text: `${event.registered_count}/${event.capacity} inscrits` },
                { icon: '💶', text: event.user_price === 0 ? 'Gratuit' : `${event.user_price}€` },
              ].filter(Boolean).map((p, i) => (
                <div key={i} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 10, padding: '8px 16px', color: '#fff', fontSize: '0.88rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span>{p.icon}</span><span>{p.text}</span>
                </div>
              ))}
            </div>
            {(permissions.can_edit || permissions.can_view_dashboard) && (
              <div className="event-detail-hero-actions" style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
                {permissions.can_edit && <a href={`/events/${event.id}/edit`} style={{ background: 'rgba(255,255,255,0.18)', color: '#fff', border: '1px solid rgba(255,255,255,0.35)', padding: '9px 20px', borderRadius: 10, textDecoration: 'none', fontWeight: 700, fontSize: '0.88rem' }}>✏️ Modifier</a>}
                {permissions.can_view_dashboard && <a href={`/events/${event.id}/dashboard`} style={{ background: 'rgba(255,255,255,0.18)', color: '#fff', border: '1px solid rgba(255,255,255,0.35)', padding: '9px 20px', borderRadius: 10, textDecoration: 'none', fontWeight: 700, fontSize: '0.88rem' }}>📊 Dashboard</a>}
                {permissions.can_edit && (
                  confirmDelete ? (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <button
                        disabled={deleting}
                        onClick={async () => {
                          setDeleting(true);
                          try {
                            const res = await fetch(`/api/v1/events/${event.id}`, {
                              method: 'DELETE',
                              headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content }
                            });
                            const data = await res.json();
                            if (data.success) {
                              setToast({ message: '🗑️ Événement supprimé', type: 'success' });
                              setTimeout(() => { window.location.href = '/events'; }, 1500);
                            } else {
                              setToast({ message: data.message || 'Erreur', type: 'error' });
                              setConfirmDelete(false);
                            }
                          } catch { setToast({ message: 'Erreur réseau', type: 'error' }); setConfirmDelete(false); }
                          finally { setDeleting(false); }
                        }}
                        style={{ background: '#dc2626', color: '#fff', border: 'none', padding: '9px 20px', borderRadius: 10, fontWeight: 700, fontSize: '0.88rem', cursor: deleting ? 'not-allowed' : 'pointer' }}
                      >{deleting ? '...' : 'Confirmer la suppression'}</button>
                      <button onClick={() => setConfirmDelete(false)} style={{ background: 'rgba(255,255,255,0.18)', color: '#fff', border: '1px solid rgba(255,255,255,0.35)', padding: '9px 20px', borderRadius: 10, fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' }}>Annuler</button>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmDelete(true)} style={{ background: 'rgba(220,38,38,0.25)', color: '#fca5a5', border: '1px solid rgba(220,38,38,0.5)', padding: '9px 20px', borderRadius: 10, fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' }}>🗑️ Supprimer</button>
                  )
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── GRILLE PRINCIPALE ─────────────────────────────────────────── */}
        <div className="event-detail-main-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>

          {/* ── COLONNE GAUCHE ─────────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24, flex: '1 1 600px', minWidth: 0 }}>

            {/* Description */}
            <div style={{ background: '#fff', borderRadius: 20, border: '1px solid rgba(124,40,55,0.10)', padding: '28px 32px' }}>
              <h2 style={{ color: '#2c0e16', fontSize: '1.15rem', fontWeight: 800, margin: '0 0 18px', paddingBottom: 12, borderBottom: '2px solid rgba(124,40,55,0.10)' }}>📝 Description</h2>
              <p style={{ color: '#4a2030', fontSize: '0.97rem', lineHeight: 1.85, margin: 0, whiteSpace: 'pre-wrap' }}>
                {event.full_description || event.description || <span style={{ color: '#9e6575', fontStyle: 'italic' }}>Aucune description fournie.</span>}
              </p>
            </div>

            {/* ── INVITATION PDF ──────────────────────────────────────── */}
            {event.invitation_pdf_url && (
              <div style={{ background: '#fff', borderRadius: 20, border: '1px solid rgba(124,40,55,0.10)', overflow: 'hidden' }}>
                <div style={{ padding: '22px 32px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                  <h2 style={{ color: '#2c0e16', fontSize: '1.15rem', fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                    📄 Invitation
                  </h2>
                  <a
                    href={event.invitation_pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,#7c2837,#a8384e)', color: '#fff', textDecoration: 'none', padding: '8px 18px', borderRadius: 10, fontWeight: 700, fontSize: '0.85rem' }}
                  >
                    ⬇️ Télécharger le PDF
                  </a>
                </div>
                <div style={{ borderTop: '2px solid rgba(124,40,55,0.08)', background: '#faf5f2' }}>
                  <iframe
                    src={event.invitation_pdf_url}
                    title="Invitation PDF"
                    style={{ width: '100%', height: 560, border: 'none', display: 'block' }}
                  />
                </div>
                {event.invitation_pdf_name && (
                  <div style={{ padding: '10px 32px', background: '#fdf8f5', borderTop: '1px solid rgba(124,40,55,0.06)', color: '#9e6575', fontSize: '0.82rem' }}>
                    📎 {event.invitation_pdf_name}
                  </div>
                )}
              </div>
            )}

            {event.is_organizer && !event.registration_open && (
              <div style={{ background: '#fff7ed', borderRadius: 20, border: '1px solid rgba(245,158,11,0.22)', padding: '22px 24px' }}>
                <div style={{ color: '#9a3412', fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  Réglage organisateur
                </div>
                <div style={{ color: '#7c2d12', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: 14 }}>
                  Les inscriptions ne sont pas ouvertes pour le moment. Activez l'option d'ouverture dans la page d'édition de l'événement pour afficher le bouton d'inscription aux participants.
                </div>
                <a href={`/events/${event.id}/edit`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#c2410c', color: '#fff', textDecoration: 'none', padding: '10px 14px', borderRadius: 10, fontWeight: 700, fontSize: '0.9rem' }}>
                  ✏️ Ouvrir les inscriptions
                </a>
              </div>
            )}

            {/* ── S'INSCRIRE : lien vers la page d'inscription ──── */}
            {!reg && event.registration_open && (
              <div style={{ background: '#fff', borderRadius: 20, border: '2px solid rgba(124,40,55,0.15)', padding: '28px 32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg,#7c2837,#a8384e)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: '1.1rem', flexShrink: 0 }}>✋</div>
                  <div>
                    <h2 style={{ color: '#2c0e16', fontSize: '1.1rem', fontWeight: 800, margin: 0 }}>Inscription</h2>
                    <p style={{ color: '#9e6575', fontSize: '0.82rem', margin: 0 }}>Confirmez votre participation à cet événement</p>
                  </div>
                </div>
                {event.spots_remaining === 0 && (
                  <div style={{ padding: '12px 16px', background: '#fffbeb', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 10, marginBottom: 18, fontSize: '0.87rem', color: '#b45309' }}>
                    ⚠️ Événement complet — vous serez ajouté à la liste d'attente.
                  </div>
                )}
                <a
                  href={`/events/${event.id}/register`}
                  style={{ display: 'block', textAlign: 'center', width: '100%', background: 'linear-gradient(135deg,#7c2837,#a8384e)', border: 'none', borderRadius: 12, padding: '15px 0', color: '#fff', fontSize: '1rem', fontWeight: 700, textDecoration: 'none' }}
                >
                  {event.spots_remaining > 0 ? `🎯 S'inscrire${event.user_price > 0 ? ` — ${event.user_price}€` : ' — Gratuit'}` : "⏳ Rejoindre la liste d'attente"}
                </a>
              </div>
            )}

            {/* ── APRÈS INSCRIPTION : progression des étapes ────────── */}
            {reg && steps.length > 1 && (
              <div style={{ background: '#fff', borderRadius: 20, border: '1px solid rgba(124,40,55,0.10)', padding: '20px 28px' }}>
                <div className="event-detail-stepper" style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                  {steps.map((s, i) => (
                    <React.Fragment key={s.key}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: s.done ? 'linear-gradient(135deg,#7c2837,#a8384e)' : '#f0e4e8', border: s.done ? 'none' : '2px solid rgba(124,40,55,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>
                          {s.done ? <span style={{ color: '#fff', fontSize: '0.9rem' }}>✓</span> : <span style={{ fontSize: '1rem' }}>{s.icon}</span>}
                        </div>
                        <span style={{ color: s.done ? '#7c2837' : '#9e6575', fontSize: '0.76rem', fontWeight: s.done ? 700 : 500, textAlign: 'center' }}>{s.label}</span>
                      </div>
                      {i < steps.length - 1 && (
                        <div style={{ height: 2, flex: 2, background: steps[i + 1].done ? 'linear-gradient(90deg,#7c2837,#a8384e)' : '#f0e4e8', marginBottom: 22, minWidth: 20 }} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}

            {/* ── ÉTAPE 2 : PAIEMENT ─────────────────────────────────── */}
            {reg && reg.payment_amount > 0 && !reg.paid && (
              <div style={{ background: '#fff', borderRadius: 20, border: '2px solid #fcd34d', padding: '28px 32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg,#f59e0b,#d97706)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: '1.1rem', flexShrink: 0 }}>2</div>
                  <div>
                    <h2 style={{ color: '#2c0e16', fontSize: '1.1rem', fontWeight: 800, margin: 0 }}>Paiement</h2>
                    <p style={{ color: '#9e6575', fontSize: '0.82rem', margin: 0 }}>Votre inscription est réservée — finalisez le paiement</p>
                  </div>
                </div>
                <div className="event-detail-split-row event-detail-inline-summary" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#fffbeb', borderRadius: 10, marginBottom: 18 }}>
                  <span style={{ color: '#b45309', fontSize: '0.9rem', fontWeight: 600 }}>Montant dû</span>
                  <span style={{ color: '#2c0e16', fontWeight: 800, fontSize: '1.2rem' }}>{reg.payment_amount}€</span>
                </div>
                <a href={`/payments/new?registration_id=${reg.id}`} style={{ display: 'block', textAlign: 'center', background: 'linear-gradient(135deg,#f59e0b,#d97706)', color: '#fff', padding: '14px', borderRadius: 12, textDecoration: 'none', fontWeight: 700, fontSize: '1rem' }}>
                  💳 Procéder au paiement
                </a>
              </div>
            )}

            {/* ── ÉTAPE 3 : QUESTIONNAIRE ────────────────────────────── */}
            {reg && reg.questionnaire_required && !reg.questionnaire_completed && reg.status !== 'cancelled' && (
              <div style={{ background: '#fff', borderRadius: 20, border: '2px solid rgba(124,40,55,0.2)', padding: '28px 32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg,#7c2837,#a8384e)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: '1.1rem', flexShrink: 0 }}>
                    {reg.payment_amount > 0 ? '3' : '2'}
                  </div>
                  <div>
                    <h2 style={{ color: '#2c0e16', fontSize: '1.1rem', fontWeight: 800, margin: 0 }}>Questionnaire</h2>
                    <p style={{ color: '#9e6575', fontSize: '0.82rem', margin: 0 }}>Complétez les informations demandées par l'organisateur</p>
                  </div>
                </div>
                <form onSubmit={handleSubmitQuestionnaire}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginBottom: 24 }}>
                    {customFields.map((field, i) => (
                      <div key={i}>
                        <label style={{ display: 'block', color: '#2c0e16', fontWeight: 600, fontSize: '0.9rem', marginBottom: 6 }}>
                          {field.label}{field.required && <span style={{ color: '#dc2626' }}> *</span>}
                        </label>
                        <QuestionnaireField field={field} />
                        {field.help && <p style={{ color: '#9e6575', fontSize: '0.8rem', margin: '4px 0 0' }}>{field.help}</p>}
                      </div>
                    ))}
                  </div>
                  <button type="submit" disabled={submittingQuestionnaire} style={{ width: '100%', background: submittingQuestionnaire ? '#e0ccd4' : 'linear-gradient(135deg,#7c2837,#a8384e)', border: 'none', borderRadius: 12, padding: '14px 0', color: '#fff', fontSize: '1rem', fontWeight: 700, cursor: submittingQuestionnaire ? 'not-allowed' : 'pointer' }}>
                    {submittingQuestionnaire ? '⏳ Envoi…' : '✅ Valider le questionnaire'}
                  </button>
                </form>
              </div>
            )}

            {/* ── QUESTIONNAIRE COMPLÉTÉ ───────────────────────────── */}
            {reg && reg.questionnaire_required && reg.questionnaire_completed && customFields.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 20, border: '1.5px solid #86efac', padding: '24px 32px' }}>
                <h2 style={{ color: '#2c0e16', fontSize: '1.1rem', fontWeight: 800, margin: '0 0 16px', paddingBottom: 12, borderBottom: '2px solid rgba(124,40,55,0.08)' }}>📋 Mes réponses au questionnaire</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {customFields.map((field, i) => {
                    const answer = (reg.custom_data || {})[field.name];
                    if (!answer && answer !== false) return null;
                    return (
                      <div key={i} className="event-detail-split-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid rgba(124,40,55,0.06)' }}>
                        <span style={{ color: '#7a4050', fontSize: '0.88rem' }}>{field.label}</span>
                        <span style={{ color: '#2c0e16', fontWeight: 600, fontSize: '0.88rem', maxWidth: '55%', textAlign: 'right' }}>{String(answer)}</span>
                      </div>
                    );
                  })}
                </div>
                <button
                  onClick={() => { setUserRegistration(r => ({ ...r, questionnaire_completed: false })); }}
                  style={{ marginTop: 16, background: 'transparent', border: '1px solid rgba(124,40,55,0.2)', color: '#7a4050', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem' }}>
                  ✏️ Modifier mes réponses
                </button>
              </div>
            )}

            {/* ── MA CARTE D'INSCRIPTION ─────────────────────────────── */}
            {reg && (
              <div style={{ background: '#fff', borderRadius: 20, border: `1.5px solid ${regStatus.border}`, padding: '28px 32px' }}>
                <h2 style={{ color: '#2c0e16', fontSize: '1.15rem', fontWeight: 800, margin: '0 0 20px', paddingBottom: 12, borderBottom: '2px solid rgba(124,40,55,0.10)' }}>🎫 Mon inscription</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {[
                    { label: 'Statut', val: <span style={{ color: regStatus.color, fontWeight: 700 }}>{regStatus.icon} {regStatus.label}</span>, bg: regStatus.bg },
                    { label: 'Inscrit(e) le', val: formatDateShort(reg.created_at) },
                    { label: 'Montant', val: reg.payment_amount > 0 ? `${reg.payment_amount}€` : 'Gratuit' },
                    reg.payment_amount > 0 && { label: 'Paiement', val: reg.paid ? '✅ Payé' : '⚠️ En attente' },
                    reg.questionnaire_required && { label: 'Questionnaire', val: reg.questionnaire_completed ? '✅ Complété' : '⏳ En attente' },
                    reg.checked_in && { label: 'Présence', val: `✅ Émargé le ${formatDateShort(reg.checked_in_at)}` },
                  ].filter(Boolean).map((row, i) => (
                    <div key={i} className="event-detail-split-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(124,40,55,0.07)', background: row.bg || 'transparent', borderRadius: row.bg ? 8 : 0, paddingLeft: row.bg ? 12 : 0, paddingRight: row.bg ? 12 : 0 }}>
                      <span style={{ color: '#7a4050', fontSize: '0.88rem' }}>{row.label}</span>
                      <span style={{ color: '#2c0e16', fontWeight: 600, fontSize: '0.88rem' }}>{row.val}</span>
                    </div>
                  ))}
                </div>
                {reg.status === 'registered' && event?.cancellation_allowed !== false && (
                  <button onClick={() => setConfirmUnregister(true)} style={{ width: '100%', marginTop: 18, background: 'transparent', border: '1.5px solid #fca5a5', color: '#dc2626', padding: '10px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}>
                    Annuler mon inscription
                  </button>
                )}
                {reg.status === 'registered' && event?.cancellation_allowed === false && (
                  <div style={{ marginTop: 18, background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.15)', borderRadius: 10, padding: '10px 14px', color: '#9e6575', fontSize: '0.85rem', textAlign: 'center' }}>
                    🔒 L'annulation d'inscription n'est pas autorisée pour cet événement
                  </div>
                )}
              </div>
            )}

            {/* ── AJOUTER UN +1 ─────────────────────────────────────── */}
            {reg && reg.status === 'registered' && event?.allow_plus_one && (event?.plus_one_remaining_for_current_user > 0) && (
              <div style={{ background: '#fff', borderRadius: 20, border: '2px dashed rgba(124,40,55,0.25)', padding: '28px 32px' }}>
                <div className="event-detail-plus-one-header" style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg,#7c2837,#a8384e)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: '1.1rem', flexShrink: 0 }}>+1</div>
                  <div style={{ flex: 1 }}>
                    <h2 style={{ color: '#2c0e16', fontSize: '1.1rem', fontWeight: 800, margin: 0 }}>Ajouter un invité</h2>
                    <p style={{ color: '#9e6575', fontSize: '0.82rem', margin: 0 }}>
                      Vous pouvez encore ajouter <strong>{event.plus_one_remaining_for_current_user}</strong> invité{event.plus_one_remaining_for_current_user > 1 ? 's' : ''} {event.plus_one_limit_for_current_user > 0 && <>sur {event.plus_one_limit_for_current_user} autorisé{event.plus_one_limit_for_current_user > 1 ? 's' : ''} pour votre niveau</>}
                    </p>
                  </div>
                  {!showPlusOneForm && (
                    <button onClick={() => setShowPlusOneForm(true)} style={{ background: 'linear-gradient(135deg,#7c2837,#a8384e)', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 16px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', flexShrink: 0 }}>
                      + Inviter
                    </button>
                  )}
                </div>
                {showPlusOneForm && (
                  <form onSubmit={handleAddPlusOne}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 18 }}>
                      <div className="event-detail-plus-one-name-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                          <label style={{ display: 'block', color: '#2c0e16', fontWeight: 600, fontSize: '0.85rem', marginBottom: 5 }}>Prénom <span style={{ color: '#dc2626' }}>*</span></label>
                          <input type="text" value={plusOneForm.first_name} onChange={e => setPlusOneForm(f => ({ ...f, first_name: e.target.value }))} required placeholder="Jean" style={inputS} />
                        </div>
                        <div>
                          <label style={{ display: 'block', color: '#2c0e16', fontWeight: 600, fontSize: '0.85rem', marginBottom: 5 }}>Nom <span style={{ color: '#dc2626' }}>*</span></label>
                          <input type="text" value={plusOneForm.last_name} onChange={e => setPlusOneForm(f => ({ ...f, last_name: e.target.value }))} required placeholder="Dupont" style={inputS} />
                        </div>
                      </div>
                      <div>
                        <label style={{ display: 'block', color: '#2c0e16', fontWeight: 600, fontSize: '0.85rem', marginBottom: 5 }}>Email <span style={{ color: '#dc2626' }}>*</span></label>
                        <input type="email" value={plusOneForm.email} onChange={e => setPlusOneForm(f => ({ ...f, email: e.target.value }))} required placeholder="jean.dupont@exemple.fr" style={inputS} />
                      </div>
                    </div>
                    <div style={{ background: '#f8f0f2', borderRadius: 10, border: '1px solid rgba(124,40,55,0.15)', padding: '14px 16px', marginBottom: 16 }}>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 10 }}>
                        <input type="checkbox" checked={plusOneForm.image_rights_accepted} onChange={e => setPlusOneForm(f => ({ ...f, image_rights_accepted: e.target.checked }))} style={{ marginTop: 2, width: 16, height: 16, accentColor: '#7c2837', flexShrink: 0 }} />
                        <span style={{ fontSize: '0.82rem', color: '#2c0e16' }}>J'accepte le <a href="/mentions-legales/droit-image" target="_blank" rel="noopener noreferrer" style={{ color: '#7c2837', fontWeight: 600 }}>droit à l'image</a> pour mon invité(e) <span style={{ color: '#dc2626' }}>*</span></span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                        <input type="checkbox" checked={plusOneForm.rules_accepted} onChange={e => setPlusOneForm(f => ({ ...f, rules_accepted: e.target.checked }))} style={{ marginTop: 2, width: 16, height: 16, accentColor: '#7c2837', flexShrink: 0 }} />
                        <span style={{ fontSize: '0.82rem', color: '#2c0e16' }}>J'accepte le <a href="/mentions-legales/reglement-interieur" target="_blank" rel="noopener noreferrer" style={{ color: '#7c2837', fontWeight: 600 }}>règlement intérieur</a> pour mon invité(e) <span style={{ color: '#dc2626' }}>*</span></span>
                      </label>
                    </div>
                    <div className="event-detail-plus-one-form-actions" style={{ display: 'flex', gap: 10 }}>
                      <button type="button" onClick={() => setShowPlusOneForm(false)} style={{ flex: '0 0 auto', background: 'transparent', border: '1.5px solid rgba(124,40,55,0.25)', color: '#7a4050', borderRadius: 12, padding: '13px 18px', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>
                        Annuler
                      </button>
                      <button type="submit" disabled={submittingPlusOne} style={{ flex: 1, background: submittingPlusOne ? '#e0ccd4' : 'linear-gradient(135deg,#7c2837,#a8384e)', border: 'none', borderRadius: 12, padding: '13px 0', color: '#fff', fontSize: '0.95rem', fontWeight: 700, cursor: submittingPlusOne ? 'not-allowed' : 'pointer' }}>
                        {submittingPlusOne ? '⏳ Ajout en cours…' : '👤 Ajouter l\'invité(e)'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {/* ── BILLETS DES +1 (paiement / questionnaire / carte) ── */}
            {(reg?.plus_ones || []).map((po) => (
              <React.Fragment key={po.registration_id}>
                {/* Paiement +1 */}
                {po.payment_amount > 0 && !po.paid && (
                  <div style={{ background: '#fff', borderRadius: 20, border: '2px solid #fcd34d', padding: '28px 32px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                      <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg,#f59e0b,#d97706)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: '0.8rem', flexShrink: 0 }}>+1💳</div>
                      <div>
                        <h2 style={{ color: '#2c0e16', fontSize: '1.1rem', fontWeight: 800, margin: 0 }}>Paiement — {po.user.name}</h2>
                        <p style={{ color: '#9e6575', fontSize: '0.82rem', margin: 0 }}>Finalisez le paiement pour votre invité(e)</p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#fffbeb', borderRadius: 10, marginBottom: 18 }}>
                      <span style={{ color: '#b45309', fontSize: '0.9rem', fontWeight: 600 }}>Montant dû</span>
                      <span style={{ color: '#2c0e16', fontWeight: 800, fontSize: '1.2rem' }}>{po.payment_amount}€</span>
                    </div>
                    <a href={`/payments/new?registration_id=${po.registration_id}`} style={{ display: 'block', textAlign: 'center', background: 'linear-gradient(135deg,#f59e0b,#d97706)', color: '#fff', padding: '14px', borderRadius: 12, textDecoration: 'none', fontWeight: 700, fontSize: '1rem' }}>
                      💳 Payer pour {po.user.name}
                    </a>
                  </div>
                )}

                {/* Questionnaire +1 */}
                {po.questionnaire_required && !po.questionnaire_completed && (
                  <div style={{ background: '#fff', borderRadius: 20, border: '2px solid rgba(124,40,55,0.2)', padding: '28px 32px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                      <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg,#7c2837,#a8384e)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: '0.8rem', flexShrink: 0 }}>+1📋</div>
                      <div>
                        <h2 style={{ color: '#2c0e16', fontSize: '1.1rem', fontWeight: 800, margin: 0 }}>Questionnaire — {po.user.name}</h2>
                        <p style={{ color: '#9e6575', fontSize: '0.82rem', margin: 0 }}>Complétez les informations pour votre invité(e)</p>
                      </div>
                    </div>
                    <form onSubmit={(e) => handleSubmitPlusOneQuestionnaire(e, po)}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginBottom: 24 }}>
                        {customFields.map((field, i) => {
                          const answers = plusOneQuestionnaires[po.registration_id] || {};
                          const setAnswer = (name, value) => setPlusOneQuestionnaires(prev => ({
                            ...prev,
                            [po.registration_id]: { ...(prev[po.registration_id] || {}), [name]: value }
                          }));
                          return (
                            <div key={i}>
                              <label style={{ display: 'block', color: '#2c0e16', fontWeight: 600, fontSize: '0.9rem', marginBottom: 6 }}>
                                {field.label}{field.required && <span style={{ color: '#dc2626' }}> *</span>}
                              </label>
                              <QuestionnaireField field={field} val={answers[field.name] || ''} onChange={v => setAnswer(field.name, v)} />
                              {field.help && <p style={{ color: '#9e6575', fontSize: '0.8rem', margin: '4px 0 0' }}>{field.help}</p>}
                            </div>
                          );
                        })}
                      </div>
                      <button type="submit" disabled={!!submittingPlusOneQuestionnaire[po.registration_id]} style={{ width: '100%', background: submittingPlusOneQuestionnaire[po.registration_id] ? '#e0ccd4' : 'linear-gradient(135deg,#7c2837,#a8384e)', border: 'none', borderRadius: 12, padding: '14px 0', color: '#fff', fontSize: '1rem', fontWeight: 700, cursor: submittingPlusOneQuestionnaire[po.registration_id] ? 'not-allowed' : 'pointer' }}>
                        {submittingPlusOneQuestionnaire[po.registration_id] ? '⏳ Envoi…' : '✅ Valider le questionnaire'}
                      </button>
                    </form>
                  </div>
                )}

                {/* Carte d'inscription +1 */}
                <div style={{ background: '#fff', borderRadius: 20, border: '1.5px solid rgba(124,40,55,0.2)', padding: '28px 32px' }}>
                  <h2 style={{ color: '#2c0e16', fontSize: '1.15rem', fontWeight: 800, margin: '0 0 20px', paddingBottom: 12, borderBottom: '2px solid rgba(124,40,55,0.10)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ background: 'linear-gradient(135deg,#7c2837,#a8384e)', color: '#fff', borderRadius: 8, padding: '2px 8px', fontSize: '0.8rem', fontWeight: 800 }}>+1</span>
                    Billet — {po.user.name}
                  </h2>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginBottom: 20 }}>
                    {[
                      { label: 'Statut', val: <span style={{ color: '#15803d', fontWeight: 700 }}>✅ Inscrit(e)</span> },
                      { label: 'Email', val: po.user.email },
                      po.payment_amount > 0 && { label: 'Paiement', val: po.paid ? '✅ Payé' : '⚠️ En attente' },
                      po.questionnaire_required && { label: 'Questionnaire', val: po.questionnaire_completed ? '✅ Complété' : '⏳ En attente' },
                      po.checked_in && { label: 'Présence', val: '✅ Émargé(e)' },
                    ].filter(Boolean).map((row, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(124,40,55,0.07)' }}>
                        <span style={{ color: '#7a4050', fontSize: '0.88rem' }}>{row.label}</span>
                        <span style={{ color: '#2c0e16', fontWeight: 600, fontSize: '0.88rem' }}>{row.val}</span>
                      </div>
                    ))}
                  </div>
                  {po.qr_code_data && (
                    <div style={{ textAlign: 'center', marginBottom: 16 }}>
                      <p style={{ color: '#9e6575', fontSize: '0.82rem', marginBottom: 12 }}>QR Code d'émargement de votre invité(e)</p>
                      <QRCodeSVG value={po.qr_code_data} size={160} level="M" style={{ borderRadius: 8 }} />
                    </div>
                  )}
                  {event?.cancellation_allowed !== false && (
                    <button
                      onClick={() => handleCancelPlusOne(po.registration_id)}
                      disabled={!!cancellingPlusOne[po.registration_id]}
                      style={{ width: '100%', background: 'transparent', border: '1.5px solid #fca5a5', color: '#dc2626', padding: '10px', borderRadius: 10, cursor: cancellingPlusOne[po.registration_id] ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.9rem' }}
                    >
                      {cancellingPlusOne[po.registration_id] ? '⏳ Annulation…' : `Annuler l'inscription de ${po.user.name}`}
                    </button>
                  )}
                </div>
              </React.Fragment>
            ))}

          </div>

          {/* ── COLONNE DROITE ─────────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, flex: '0 1 340px', minWidth: 280 }}>

            {/* Infos pratiques */}
            <div style={{ background: '#fff', borderRadius: 20, border: '1px solid rgba(124,40,55,0.10)', padding: '24px 26px' }}>
              <h3 style={{ color: '#2c0e16', fontSize: '1rem', fontWeight: 800, margin: '0 0 18px' }}>📅 Informations pratiques</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {[
                  { icon: '🗓️', label: 'Début', val: `${formatDateShort(event.start_date)}\n${formatTime(event.start_date)}` },
                  event.end_date && { icon: '🏁', label: 'Fin', val: `${formatDateShort(event.end_date)}\n${formatTime(event.end_date)}` },
                  { icon: '📍', label: 'Lieu', val: event.location },
                  { icon: '👤', label: 'Organisateur', val: event.organizer?.name },
                ].filter(Boolean).map((row, i, arr) => (
                  <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 0', borderBottom: i < arr.length - 1 ? '1px solid rgba(124,40,55,0.07)' : 'none' }}>
                    <span style={{ fontSize: '1rem', flexShrink: 0, marginTop: 1 }}>{row.icon}</span>
                    <div>
                      <div style={{ color: '#9e6575', fontSize: '0.76rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>{row.label}</div>
                      <div style={{ color: '#2c0e16', fontWeight: 600, fontSize: '0.88rem', whiteSpace: 'pre-line' }}>{row.val}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Carte */}
            {event.location && (
              <div style={{ background: '#fff', borderRadius: 20, border: '1px solid rgba(124,40,55,0.10)', overflow: 'hidden' }}>
                <button
                  onClick={() => setShowMap(!showMap)}
                  style={{
                    width: '100%', padding: '16px 26px', background: 'none', border: 'none',
                    cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                  }}
                >
                  <span style={{ color: '#2c0e16', fontSize: '1rem', fontWeight: 800 }}>🗺️ Voir sur la carte</span>
                  <span style={{ color: '#9e6575', fontSize: '1.1rem', transition: 'transform 0.3s', transform: showMap ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                </button>
                {showMap && (
                  <div style={{ padding: '0 0 4px' }}>
                    <iframe
                      title="Carte du lieu"
                      width="100%"
                      height="250"
                      style={{ border: 'none', borderRadius: '0 0 16px 16px' }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://maps.google.com/maps?q=${encodeURIComponent(event.location)}&output=embed&z=15`}
                    />
                    <div style={{ textAlign: 'center', padding: '8px 0' }}>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#7c2837', fontSize: '0.82rem', fontWeight: 600, textDecoration: 'none' }}
                      >
                        📍 Ouvrir dans Google Maps
                      </a>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Jauge */}
            <div style={{ background: '#fff', borderRadius: 20, border: '1px solid rgba(124,40,55,0.10)', padding: '24px 26px' }}>
              <h3 style={{ color: '#2c0e16', fontSize: '1rem', fontWeight: 800, margin: '0 0 14px' }}>👥 Jauge</h3>
              <div className="event-detail-split-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: '0.85rem' }}>
                <span style={{ color: '#7a4050' }}>{event.registered_count} inscrits</span>
                <span style={{ color: '#9e6575' }}>sur {event.capacity}</span>
              </div>
              <div style={{ background: '#f0e4e8', borderRadius: 8, height: 10, overflow: 'hidden', marginBottom: 10 }}>
                <div style={{ height: '100%', borderRadius: 8, width: `${fillPct}%`, background: fillPct >= 90 ? 'linear-gradient(90deg,#dc2626,#ef4444)' : fillPct >= 70 ? 'linear-gradient(90deg,#f59e0b,#d97706)' : 'linear-gradient(90deg,#7c2837,#a8384e)', transition: 'width 0.5s' }} />
              </div>
              <div className="event-detail-split-row" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                <span style={{ color: fillPct >= 100 ? '#dc2626' : '#15803d', fontWeight: 700 }}>
                  {fillPct >= 100 ? '🔴 Complet' : `🟢 ${event.spots_remaining} place${event.spots_remaining > 1 ? 's' : ''} dispo.`}
                </span>
                <span style={{ color: '#9e6575' }}>{fillPct}%</span>
              </div>
              {event.statistics?.waitlist_count > 0 && (
                <div style={{ marginTop: 10, padding: '8px 12px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, color: '#b45309', fontSize: '0.82rem', fontWeight: 600 }}>
                  ⏳ {event.statistics.waitlist_count} en liste d'attente
                </div>
              )}
            </div>

            {/* Tarif */}
            <div style={{ background: 'linear-gradient(135deg,#7c2837,#a8384e)', borderRadius: 20, padding: '24px 26px', textAlign: 'center' }}>
              <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Tarif pour vous</div>
              <div style={{ color: '#fff', fontSize: '2.2rem', fontWeight: 800, lineHeight: 1 }}>{event.user_price === 0 ? 'Gratuit' : `${event.user_price}€`}</div>
              {event.user_price > 0 && <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.78rem', marginTop: 8 }}>🔒 Paiement sécurisé</div>}
              {!reg && !event.registration_open && (
                <div style={{ marginTop: 14, color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>Inscriptions fermées</div>
              )}
            </div>

            {/* Note */}
            {event.status === 'completed' && event.statistics?.average_rating > 0 && (
              <div style={{ background: '#fff', borderRadius: 20, border: '1px solid rgba(124,40,55,0.10)', padding: '24px 26px', textAlign: 'center' }}>
                <div style={{ color: '#9e6575', fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Note des participants</div>
                <div style={{ color: '#2c0e16', fontSize: '2rem', fontWeight: 800 }}>⭐ {event.statistics.average_rating}<span style={{ fontSize: '1rem', color: '#9e6575' }}>/5</span></div>
              </div>
            )}
          </div>
        </div>

        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
};

export default EventDetail;
